import { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  LinkIcon,
  ServerStackIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import type { UseMutationResult } from '@tanstack/react-query'

import {
  Badge,
  Button,
  Input,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Select,
  SelectOption,
} from '../../components/ui'
import type {
  RevenueRedshiftConnectorOption,
  RevenueRedshiftDataSourceConfig,
  RevenueRedshiftDataSourceUpdate,
  RevenueRedshiftHealth,
} from './redshiftDataSourceTypes'

interface RedshiftDataSourcePanelProps {
  appletName: string
  defaultSchema: string
  expectedTables: string[]
  config?: RevenueRedshiftDataSourceConfig
  health?: RevenueRedshiftHealth
  connectors: RevenueRedshiftConnectorOption[]
  isLoading?: boolean
  saveMutation: UseMutationResult<
    RevenueRedshiftDataSourceConfig,
    Error,
    RevenueRedshiftDataSourceUpdate
  >
  clearMutation: UseMutationResult<RevenueRedshiftDataSourceConfig, Error, void>
  testMutation: UseMutationResult<
    RevenueRedshiftHealth,
    Error,
    RevenueRedshiftDataSourceUpdate
  >
}

export default function RedshiftDataSourcePanel({
  appletName,
  defaultSchema,
  expectedTables,
  config,
  health,
  connectors,
  isLoading,
  saveMutation,
  clearMutation,
  testMutation,
}: RedshiftDataSourcePanelProps) {
  const [connectorId, setConnectorId] = useState('')
  const [schema, setSchema] = useState(defaultSchema)

  useEffect(() => {
    setConnectorId(config?.connector_id ?? '')
    setSchema(config?.redshift_schema || defaultSchema)
  }, [config?.connector_id, config?.redshift_schema, defaultSchema])

  const selectedConnector = useMemo(
    () => connectors.find(connector => connector.connector_id === connectorId),
    [connectorId, connectors],
  )
  const connected = Boolean(config?.is_connected || health?.redshift_connected)
  const trimmedSchema = schema.trim() || defaultSchema
  const canSubmit = Boolean(connectorId && trimmedSchema && connectorId !== 'env:redshift')

  if (isLoading) {
    return (
      <Panel>
        <PanelBody>
          <div className="flex h-32 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            Loading Redshift connectors...
          </div>
        </PanelBody>
      </Panel>
    )
  }

  return (
    <Panel>
      <PanelHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <PanelTitle>Redshift Connection</PanelTitle>
            <PanelDescription>
              Bind {appletName} to the saved Redshift connector and seeded schema used for close data.
            </PanelDescription>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge variant={connected ? 'success' : 'warning'}>
              Redshift {connected ? 'connected' : 'required'}
            </Badge>
            <Badge variant="secondary">{config?.mode ?? health?.data_source_mode ?? 'unconfigured'}</Badge>
            {trimmedSchema && (
              <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
                schema {trimmedSchema}
              </Badge>
            )}
          </div>
        </div>
      </PanelHeader>
      <PanelBody className="space-y-5">
        <div className="grid grid-cols-[minmax(0,1fr)_260px] gap-4 max-lg:grid-cols-1">
          <div className="space-y-2">
            <label className="text-sm font-medium text-charcoal dark:text-gray-100">
              Redshift Connector
            </label>
            <Select
              value={connectorId}
              onValueChange={setConnectorId}
              placeholder={connectors.length ? 'Select a connector' : 'No Redshift connectors available'}
              disabled={!connectors.length}
              icon={<LinkIcon />}
            >
              {connectors.map(connector => (
                <SelectOption key={connector.connector_id} value={connector.connector_id}>
                  {connector.connector_name}
                </SelectOption>
              ))}
            </Select>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Current: {config?.connector_name ?? 'No saved connector'}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-charcoal dark:text-gray-100">
              Redshift Schema
            </label>
            <Input
              value={schema}
              onChange={event => setSchema(event.target.value)}
              placeholder={defaultSchema}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Default: {defaultSchema}
            </p>
          </div>
        </div>

        <div className="grid gap-3 max-sm:pr-16 sm:flex sm:flex-wrap sm:items-center">
          <Button
            variant="brand"
            className="w-full sm:w-auto"
            onClick={() =>
              saveMutation.mutate({
                connector_id: connectorId,
                redshift_schema: trimmedSchema,
              })
            }
            disabled={!canSubmit || saveMutation.isPending}
          >
            {saveMutation.isPending ? <ArrowPathIcon className="animate-spin" /> : <CheckCircleIcon />}
            {saveMutation.isPending ? 'Saving' : 'Save Configuration'}
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() =>
              testMutation.mutate({
                connector_id: connectorId,
                redshift_schema: trimmedSchema,
              })
            }
            disabled={!canSubmit || testMutation.isPending}
          >
            {testMutation.isPending ? <ArrowPathIcon className="animate-spin" /> : <ServerStackIcon />}
            {testMutation.isPending ? 'Testing' : 'Test Connection'}
          </Button>
          {config?.mode === 'saved' && (
            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              onClick={() => {
                if (window.confirm(`Clear the saved ${appletName} Redshift connector?`)) {
                  clearMutation.mutate()
                }
              }}
              disabled={clearMutation.isPending}
            >
              {clearMutation.isPending ? <ArrowPathIcon className="animate-spin" /> : <TrashIcon />}
              {clearMutation.isPending ? 'Clearing' : 'Clear Saved Connector'}
            </Button>
          )}
        </div>

        <MutationStatus
          saved={saveMutation.isSuccess}
          saveError={saveMutation.error}
          cleared={clearMutation.isSuccess}
          clearError={clearMutation.error}
          tested={testMutation.data}
          testError={testMutation.error}
        />

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge variant={selectedConnector?.is_healthy ? 'success' : 'secondary'}>
              {selectedConnector?.is_healthy ? 'Connector healthy' : 'Connector not selected'}
            </Badge>
            <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
              Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
            </Badge>
            {config?.host && (
              <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
                {config.host}
              </Badge>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {expectedTables.map(table => (
              <Badge key={table} variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
                {table}
              </Badge>
            ))}
          </div>
        </div>
      </PanelBody>
    </Panel>
  )
}

function MutationStatus({
  saved,
  saveError,
  cleared,
  clearError,
  tested,
  testError,
}: {
  saved: boolean
  saveError: Error | null
  cleared: boolean
  clearError: Error | null
  tested?: RevenueRedshiftHealth
  testError: Error | null
}) {
  if (saveError || clearError || testError) {
    return (
      <p className="text-sm text-rose-600 dark:text-rose-400">
        {(saveError || clearError || testError)?.message ?? 'Redshift connection request failed.'}
      </p>
    )
  }

  if (tested) {
    return (
      <Badge variant={tested.redshift_connected ? 'success' : 'warning'} className="w-fit">
        {tested.message || tested.status}
      </Badge>
    )
  }

  if (saved) {
    return <Badge variant="success" className="w-fit">Configuration saved</Badge>
  }

  if (cleared) {
    return <Badge variant="secondary" className="w-fit">Saved connector cleared</Badge>
  }

  return null
}
